import { generateCommunicationAsync } from 'actions';
import LoadingIndicator from 'components/LoadingIndicator';
import MultiTermInput from 'components/MultiTermInput';
import Navbar from 'components/Navbar';
import SettingSidebar from 'components/Navbar/SettingsSidebar';
import SelectRedesign from 'components/Select/SelectRedesign';
import { platformNames } from 'constants/platform';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from 'selectors/auth';
import { getGenerateLoading } from 'selectors/communications';
import { Platform } from 'types';
import { NAV_BAR_HEIGHT_REM } from 'utils/dimensions';

type FormValues = {
  subject: string;
  body: string;
  sender: string;
};

const GenerateCommunication: React.FC = (): JSX.Element => {
  const dispatch = useDispatch();

  const loading = useSelector(getGenerateLoading);
  const user = useSelector(getUser);

  const { handleSubmit, register } = useForm<FormValues>({
    defaultValues: {
      body: '',
      subject: '',
      sender: user.email || '',
    },
  });

  const [platform, setPlatform] = useState<Platform | 'all' | ''>('');
  const [type, setType] = useState<string>('app');
  const [recipients, setRecipients] = useState<string[]>([]);

  const onSubmit = ({ body, subject, sender }: FormValues): void => {
    dispatch(
      generateCommunicationAsync({
        body,
        subject,
        platform: platform || 'all',
        integration: type,
        sender,
        recipients,
      })
    );
  };

  const handlePlatformChange = (value: string): void => {
    setPlatform(value as Platform);
  };

  const handleIntegrationChange = (value: string): void => {
    setType(value);
  };

  const handleRecipientsChange = (value: string): void => {
    if (value) {
      setRecipients((r) => [...new Set([...r, value])]);
    }
  };

  const handleRecipientsDelete = (value: string): void => {
    setRecipients((recip) => recip.filter((r) => r !== value));
  };

  const platformOptions = (Object.keys(platformNames) as Array<keyof typeof platformNames>).map(
    (p) => ({
      label: platformNames[p] || '',
      value: p || '',
    })
  );

  const integrationOptions = [
    { label: 'Insight', value: 'api' },
    { label: 'Prevent', value: 'app' },
  ];

  const warningBarOffset =
    document != null &&
    document.getElementById != null &&
    document.getElementById('customer-warning')?.offsetHeight;

  return (
    <div
      className="flex flex-col bg-white min-w-screen"
      style={{
        height: `calc(100vh - ${warningBarOffset}px)`,
      }}
    >
      <Navbar />
      <div
        className="flex flex-row"
        style={{
          height: `calc(100vh - ${warningBarOffset}px - ${NAV_BAR_HEIGHT_REM}rem)`,
        }}
      >
        <SettingSidebar />

        <div className="py-6 px-6 flex flex-col gap-10 w-full">
          <div className="w-full flex flex-row justify-between h-8 items-center">
            <span className="text-heading-2">Generate Communication</span>
            <button
              type="button"
              className="button button--primary font-bold h-8 w-37 justify-center"
              onClick={handleSubmit(onSubmit)}
            >
              {loading ? <LoadingIndicator size="5" /> : 'Generate'}
            </button>
          </div>

          <div className="h-full w-full flex flex-row gap-10 text-body overflow-auto">
            <div className="w-152 flex flex-col items-start gap-2">
              <div className="w-full flex flex-row gap-2">
                <label htmlFor="sender" className="flex flex-col gap-1 flex-1">
                  <div className="w-full flex flex-row gap-1 ">
                    <span>Sender</span>
                    <span className="litlingo-red-color">*</span>
                  </div>

                  <input
                    id="sender"
                    ref={register({ required: true, pattern: /\S/i })}
                    name="sender"
                    placeholder="Sender"
                    className="form-input max-h-8 flex-1 p-2 text-body rounded placeholder-italic placeholder-litlingo-gray-6"
                  />
                </label>

                <div className="w-37 flex flex-col gap-1">
                  <div className="w-full flex flex-row gap-1 ">
                    <span>Subject</span>
                  </div>
                  <SelectRedesign
                    placeholder="Platform"
                    onChange={handlePlatformChange}
                    options={platformOptions}
                    value={platform}
                    data-testid="platform-select"
                  />
                </div>
                <div className="w-37 flex flex-col gap-1">
                  <div className="w-full flex flex-row gap-1 ">
                    <span>Integration</span>
                    <span className="litlingo-red-color">*</span>
                  </div>
                  <SelectRedesign
                    placeholder="Integration type"
                    onChange={handleIntegrationChange}
                    options={integrationOptions}
                    value={type}
                    data-testid="platform-select"
                  />
                </div>
              </div>

              <div className="w-full flex flex-col gap-1">
                <div className="flex flex-row gap-1 ">
                  <span>Recipients</span>
                </div>
                <MultiTermInput
                  terms={recipients}
                  onChange={handleRecipientsChange}
                  onDelete={handleRecipientsDelete}
                />
              </div>

              <div className="mt-4 w-full flex flex-col gap-1">
                Message Information
                <label htmlFor="subject" className="flex flex-col gap-1">
                  <input
                    id="subject"
                    ref={register({ required: true, pattern: /\S/i })}
                    name="subject"
                    placeholder="Subject"
                    className="form-input max-h-8 flex-1 p-2 text-body rounded placeholder-italic placeholder-litlingo-gray-6"
                  />
                </label>
                <textarea
                  id="body"
                  ref={register({ required: true, pattern: /\S/i })}
                  name="body"
                  rows={25}
                  placeholder="Write the content of the communication."
                  className="form-input w-full p-2 text-body rounded resize-none placeholder-italic placeholder-litlingo-gray-6"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateCommunication;
